import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft } from "lucide-react";
import { useId, useState } from "react";
import { useNavigate } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Badge, Breadcrumb, Button, ErrorAlert, Section } from "@/components/ui";
import { DomainPage, Heading, HeadTile, Hint, RuleRow } from "@/components/domains/parts";
import { MAX_BULK_TRANSFERS, parseTransferPairs } from "@/lib/bulkTransfers";
import { domainKeys, domainsApi, type BulkTransferResponse } from "@/lib/domains";
import { formatMoney } from "@/lib/format";

/**
 * `/domains/transfer/bulk` (PHP-3941): up to 100 transfers in at once, pasted as "domain, auth code" lines or read
 * from a two-column CSV. Each name is checked first; ineligible ones are listed with the reason and left out, and the
 * eligible ones go to one checkout with one payment.
 */
export function BulkTransferPage() {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [checked, setChecked] = useState<BulkTransferResponse | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const textId = useId();
  const hintId = useId();
  const fileId = useId();

  const pairs = parseTransferPairs(text);
  const tooMany = pairs.length > MAX_BULK_TRANSFERS;

  const check = useMutation({
    mutationFn: () => domainsApi.bulkTransfers(orgId, pairs),
    onSuccess: setChecked,
  });
  const add = useMutation({
    mutationFn: () =>
      domainsApi.bulkTransfers(
        orgId,
        pairs.filter((p) => eligible.has(p.fqdn)),
        true,
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
      void navigate("/domains/checkout");
    },
  });

  const results = checked?.results ?? [];
  const eligible = new Set(results.filter((r) => r.eligible).map((r) => r.fqdn));
  const refused = results.filter((r) => !r.eligible);
  const total = results.reduce((sum, r) => sum + (r.eligible ? (r.price_cents ?? 0) : 0), 0);

  function edit(value: string) {
    setText(value);
    setChecked(null);
    check.reset();
    add.reset();
  }

  async function readFile(file: File) {
    setFileError(null);
    if (file.size > 256 * 1024) {
      setFileError("That file is too large. Use a CSV with two columns: domain and auth code.");
      return;
    }
    edit(await file.text());
  }

  return (
    <DomainPage>
      <Breadcrumb items={[{ label: "Domains", to: "/domains" }, { label: "Bulk transfer" }]} />
      <Heading
        size={24}
        tile={
          <HeadTile tone="pending">
            <ArrowRightLeft className="size-5" aria-hidden />
          </HeadTile>
        }
        title="Transfer domains in bulk"
        subtitle={`Move up to ${MAX_BULK_TRANSFERS} domains to CortexGrip in one order and one payment`}
      />

      <Section title="Domains and authorization codes">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={textId} className="text-[13px] font-semibold text-fg">
            One &quot;domain, auth code&quot; pair per line
          </label>
          <textarea
            id={textId}
            value={text}
            onChange={(e) => edit(e.target.value)}
            rows={8}
            spellCheck={false}
            autoComplete="off"
            aria-describedby={hintId}
            aria-invalid={tooMany || undefined}
            placeholder={"saltandbloom.com, Xy7-q2Lp\nsaltandbloom.co.uk, 9Fz!k3Ra"}
            className="min-h-40 resize-y rounded-xl border border-border bg-surface px-3 py-2 font-mono text-[14px] text-fg outline-none placeholder:text-fg-subtle focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20"
          />
          <p
            id={hintId}
            className={tooMany ? "text-xs text-red-600 dark:text-red-400" : "text-xs text-fg-subtle"}
          >
            {tooMany
              ? `That's ${pairs.length} domains. Transfer up to ${MAX_BULK_TRANSFERS} at a time.`
              : `${pairs.length} of up to ${MAX_BULK_TRANSFERS} domains. Get each code from the current registrar's domain settings.`}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={fileId} className="text-[13px] font-semibold text-fg">
            Or upload a CSV (two columns: domain, auth code)
          </label>
          <input
            id={fileId}
            type="file"
            accept=".csv,text/csv,text/plain"
            aria-invalid={Boolean(fileError) || undefined}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void readFile(file);
              e.target.value = "";
            }}
            className="text-sm text-fg-muted file:mr-3 file:cursor-pointer file:rounded-lg file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-fg file:transition-colors hover:file:bg-border"
          />
          {fileError && (
            <p role="alert" className="text-xs text-red-600 dark:text-red-400">
              {fileError}
            </p>
          )}
        </div>
        <Hint tone="warning" title="Keep these codes private">
          Anyone with a domain&apos;s code can move it. We only use them to ask the current registrars to
          release the domains.
        </Hint>
        <ErrorAlert error={check.error} />
        <div>
          <Button
            variant="brand"
            size="lg"
            loading={check.isPending}
            disabledReason={
              pairs.length === 0
                ? "Add at least one domain and its code"
                : tooMany
                  ? `Transfer up to ${MAX_BULK_TRANSFERS} at a time`
                  : undefined
            }
            onClick={() => check.mutate()}
          >
            Check {pairs.length > 1 ? `${pairs.length} domains` : "domain"}
          </Button>
        </div>
      </Section>

      {checked && (
        <Section
          title="Eligibility"
          subtitle={`${eligible.size} can be transferred${refused.length ? `, ${refused.length} can't` : ""}`}
        >
          <ul aria-label="Eligibility of each domain" className="flex flex-col gap-3.5">
            {results.map((r, i) => (
              <li key={`${r.fqdn}-${i}`}>
                <RuleRow first={i === 0}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium break-all text-fg">{r.fqdn || "(no domain)"}</p>
                    {r.reason && <p className="text-xs text-fg-subtle">{r.reason}</p>}
                  </div>
                  {r.eligible ? (
                    <Badge size="md" tone="success">
                      {r.price_cents !== null
                        ? `${formatMoney(r.price_cents, checked.currency)} + VAT`
                        : "Eligible"}
                    </Badge>
                  ) : (
                    <Badge size="md" tone="danger">
                      Left out
                    </Badge>
                  )}
                </RuleRow>
              </li>
            ))}
          </ul>
          <ErrorAlert error={add.error} />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="brand"
              size="lg"
              loading={add.isPending}
              disabledReason={eligible.size === 0 ? "None of these domains can be transferred" : undefined}
              onClick={() => add.mutate()}
            >
              Add {eligible.size} to cart and check out
            </Button>
            {eligible.size > 0 && (
              <p className="text-sm text-fg-subtle">
                {formatMoney(total, checked.currency)} + VAT · each includes a year added to the registration
              </p>
            )}
          </div>
        </Section>
      )}
    </DomainPage>
  );
}
